import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  LayoutDashboard, Target, FileText, Bell, Calendar, CheckSquare,
  LogOut, User, ChevronRight, Clock, TrendingUp, AlertCircle,
  Loader2, Menu, X, ExternalLink, BarChart3, Flag
} from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompanyDashboard = () => {
  const { user, logout, loading: authLoading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [milestones, setMilestones] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [notifications, setNotifications] = useState({ notifications: [], unread_count: 0 });
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const currentUser = location.state?.user || user;

  useEffect(() => {
    if (!currentUser && !authLoading) {
      checkAuth();
    }
  }, [currentUser, authLoading, checkAuth]);

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [currentUser]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [milestonesRes, kpisRes, tasksRes, docsRes, notifsRes, meetingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/portal/company/milestones`, { withCredentials: true }),
        axios.get(`${API_URL}/api/portal/company/kpis`, { withCredentials: true }),
        axios.get(`${API_URL}/api/portal/company/tasks`, { withCredentials: true }),
        axios.get(`${API_URL}/api/portal/documents`, { withCredentials: true }),
        axios.get(`${API_URL}/api/portal/notifications`, { withCredentials: true }),
        axios.get(`${API_URL}/api/portal/meetings`, { withCredentials: true })
      ]);
      
      setMilestones(milestonesRes.data);
      setKpis(kpisRes.data);
      setTasks(tasksRes.data);
      setDocuments(docsRes.data);
      setNotifications(notifsRes.data);
      setMeetings(meetingsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      if (err.response?.status === 401) {
        navigate('/portal/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await axios.put(
        `${API_URL}/api/portal/company/tasks/${taskId}?status=${newStatus}`,
        {},
        { withCredentials: true }
      );
      setTasks(prev => prev.map(t => 
        t.task_id === taskId ? { ...t, status: newStatus } : t
      ));
    } catch (err) {
      console.error('Error updating task:', err);
    }
  };

  if (authLoading || (!currentUser && !loading)) {
    return (
      <div className="min-h-screen bg-[#F8FAFB] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#60B5AF] animate-spin" />
      </div>
    );
  }

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'kpis', label: 'KPI Dashboard', icon: BarChart3 },
    { id: 'milestones', label: 'Milestones', icon: Flag },
    { id: 'tasks', label: 'Tasks', icon: CheckSquare },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'notifications', label: 'Notifications', icon: Bell, badge: notifications.unread_count },
  ];

  // Calculate summary stats
  const completedMilestones = milestones.filter(m => m.status === 'completed').length;
  const completedTasks = tasks.filter(t => t.status === 'done').length;
  const overdueTasks = tasks.filter(t => t.status !== 'done' && new Date(t.due_date) < new Date()).length;

  return (
    <div className="min-h-screen bg-[#F8FAFB] flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#1A3A4A] transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-200`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/10">
            <a href="/" className="font-heading text-2xl font-bold text-white tracking-tight">
              HÜBNER<span className="text-[#60B5AF]">.</span>
            </a>
            <p className="font-body text-white/40 text-xs mt-1">Company Portal</p>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-body text-sm transition-all ${
                  activeTab === item.id 
                    ? 'bg-[#60B5AF] text-white' 
                    : 'text-white/60 hover:bg-white/5 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
                {item.badge > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>

          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-4">
              {currentUser?.picture ? (
                <img src={currentUser.picture} alt="" className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#60B5AF]/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#60B5AF]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-body font-medium text-white text-sm truncate">{currentUser?.name}</p>
                <p className="font-body text-white/40 text-xs truncate">{currentUser?.email}</p>
              </div>
            </div>
            <Button
              onClick={logout}
              variant="ghost"
              className="w-full justify-start text-white/60 hover:text-white hover:bg-white/5"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-[#1A3A4A]/10 px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)} className="p-2">
          <Menu className="w-6 h-6 text-[#1A3A4A]" />
        </button>
        <span className="font-heading font-bold text-[#1A3A4A]">HÜBNER<span className="text-[#60B5AF]">.</span></span>
        <div className="w-10" />
      </div>

      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className="flex-1 lg:p-8 p-4 pt-20 lg:pt-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-[#60B5AF] animate-spin" />
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h1 className="font-heading text-2xl font-bold text-[#1A3A4A]">
                    Welcome back, {currentUser?.name?.split(' ')[0]}
                  </h1>
                  <p className="font-body text-[#1A3A4A]/60">Here's your company progress overview</p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard 
                    title="Milestones Completed" 
                    value={`${completedMilestones}/${milestones.length}`}
                    icon={Flag}
                    progress={milestones.length > 0 ? (completedMilestones / milestones.length) * 100 : 0}
                  />
                  <StatCard 
                    title="Tasks Done" 
                    value={`${completedTasks}/${tasks.length}`}
                    icon={CheckSquare}
                    progress={tasks.length > 0 ? (completedTasks / tasks.length) * 100 : 0}
                  />
                  <StatCard 
                    title="Overdue Items" 
                    value={overdueTasks}
                    icon={AlertCircle}
                    alert={overdueTasks > 0}
                  />
                  <StatCard 
                    title="Upcoming Meetings" 
                    value={meetings.length}
                    icon={Calendar}
                  />
                </div>

                {/* Recent Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Active Tasks */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading font-semibold text-[#1A3A4A]">Active Tasks</h3>
                      <button 
                        onClick={() => setActiveTab('tasks')}
                        className="text-sm text-[#60B5AF] hover:underline font-body flex items-center gap-1"
                      >
                        View all <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                    {tasks.filter(t => t.status !== 'done').slice(0, 4).map((task) => (
                      <div key={task.task_id} className="flex items-center justify-between py-3 border-b border-[#1A3A4A]/5 last:border-0">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={task.status === 'done'}
                            onChange={() => updateTaskStatus(task.task_id, 'done')}
                            className="w-4 h-4 rounded border-[#1A3A4A]/20 text-[#60B5AF] focus:ring-[#60B5AF]"
                          />
                          <div>
                            <p className="font-body font-medium text-[#1A3A4A] text-sm">{task.title}</p>
                            {task.due_date && (
                              <p className={`font-body text-xs ${
                                new Date(task.due_date) < new Date() ? 'text-red-500' : 'text-[#1A3A4A]/40'
                              }`}>
                                Due: {task.due_date}
                              </p>
                            )}
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-body ${
                          task.priority === 'high' ? 'bg-red-100 text-red-700' :
                          task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {task.priority}
                        </span>
                      </div>
                    ))}
                    {tasks.filter(t => t.status !== 'done').length === 0 && (
                      <p className="font-body text-[#1A3A4A]/40 text-center py-8">All tasks completed!</p>
                    )}
                  </div>

                  {/* Milestones Progress */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading font-semibold text-[#1A3A4A]">Milestones</h3>
                      <button 
                        onClick={() => setActiveTab('milestones')}
                        className="text-sm text-[#60B5AF] hover:underline font-body flex items-center gap-1"
                      >
                        View all <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                    {milestones.slice(0, 4).map((milestone) => (
                      <div key={milestone.milestone_id} className="py-3 border-b border-[#1A3A4A]/5 last:border-0">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-body font-medium text-[#1A3A4A] text-sm">{milestone.title}</p>
                          <span className={`px-2 py-1 rounded text-xs font-body ${
                            milestone.status === 'completed' ? 'bg-green-100 text-green-700' :
                            milestone.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            milestone.status === 'overdue' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {milestone.status}
                          </span>
                        </div>
                        <div className="w-full bg-[#1A3A4A]/10 rounded-full h-2">
                          <div 
                            className="bg-[#60B5AF] h-2 rounded-full transition-all"
                            style={{ width: `${milestone.progress_percentage}%` }}
                          />
                        </div>
                      </div>
                    ))}
                    {milestones.length === 0 && (
                      <p className="font-body text-[#1A3A4A]/40 text-center py-8">No milestones yet</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* KPIs Tab */}
            {activeTab === 'kpis' && (
              <div className="space-y-6">
                <h1 className="font-heading text-2xl font-bold text-[#1A3A4A]">KPI Dashboard</h1>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {kpis.map((kpi) => (
                    <div key={kpi.metric_id} className="bg-white rounded-2xl p-6 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <p className="font-body text-sm text-[#1A3A4A]/60">{kpi.name}</p>
                        <span className={`flex items-center text-xs font-body ${
                          kpi.trend === 'up' ? 'text-green-600' : 
                          kpi.trend === 'down' ? 'text-red-600' : 
                          'text-[#1A3A4A]/40'
                        }`}>
                          {kpi.trend === 'up' && <TrendingUp className="w-3 h-3 mr-1" />}
                          {kpi.trend === 'down' && <TrendingUp className="w-3 h-3 mr-1 rotate-180" />}
                          {kpi.trend}
                        </span>
                      </div>
                      <p className="font-heading text-3xl font-bold text-[#1A3A4A]">
                        {kpi.value.toLocaleString()} <span className="text-base font-normal text-[#1A3A4A]/40">{kpi.unit}</span>
                      </p>
                      <div className="mt-4">
                        <div className="flex justify-between text-xs font-body mb-1">
                          <span className="text-[#1A3A4A]/40">Progress to target</span>
                          <span className="text-[#1A3A4A]/60">{Math.round((kpi.value / kpi.target) * 100)}%</span>
                        </div>
                        <div className="w-full bg-[#1A3A4A]/10 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all ${
                              kpi.value >= kpi.target ? 'bg-green-500' : 'bg-[#60B5AF]'
                            }`}
                            style={{ width: `${Math.min((kpi.value / kpi.target) * 100, 100)}%` }}
                          />
                        </div>
                        <p className="text-xs font-body text-[#1A3A4A]/40 mt-1">
                          Target: {kpi.target.toLocaleString()} {kpi.unit}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                {kpis.length === 0 && (
                  <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                    <BarChart3 className="w-12 h-12 text-[#1A3A4A]/20 mx-auto mb-4" />
                    <p className="font-body text-[#1A3A4A]/40">No KPIs configured yet</p>
                  </div>
                )}
              </div>
            )}

            {/* Milestones Tab */}
            {activeTab === 'milestones' && (
              <div className="space-y-6">
                <h1 className="font-heading text-2xl font-bold text-[#1A3A4A]">Milestones</h1>
                
                <div className="space-y-4">
                  {milestones.map((milestone) => (
                    <div key={milestone.milestone_id} className="bg-white rounded-2xl p-6 shadow-sm">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-heading font-semibold text-[#1A3A4A]">{milestone.title}</h3>
                          <p className="font-body text-sm text-[#1A3A4A]/60">{milestone.description}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${
                          milestone.status === 'completed' ? 'bg-green-100 text-green-700' :
                          milestone.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          milestone.status === 'overdue' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {milestone.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <div className="w-full bg-[#1A3A4A]/10 rounded-full h-3">
                            <div 
                              className="bg-[#60B5AF] h-3 rounded-full transition-all"
                              style={{ width: `${milestone.progress_percentage}%` }}
                            />
                          </div>
                        </div>
                        <span className="font-body font-medium text-[#1A3A4A]">{milestone.progress_percentage}%</span>
                      </div>
                      <p className="font-body text-xs text-[#1A3A4A]/40 mt-2 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Due: {milestone.due_date}
                      </p>
                    </div>
                  ))}
                </div>
                {milestones.length === 0 && (
                  <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                    <Flag className="w-12 h-12 text-[#1A3A4A]/20 mx-auto mb-4" />
                    <p className="font-body text-[#1A3A4A]/40">No milestones yet</p>
                  </div>
                )}
              </div>
            )}

            {/* Tasks Tab */}
            {activeTab === 'tasks' && (
              <div className="space-y-6">
                <h1 className="font-heading text-2xl font-bold text-[#1A3A4A]">Tasks</h1>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* To Do */}
                  <div className="bg-white rounded-2xl p-4 shadow-sm">
                    <h3 className="font-body font-medium text-[#1A3A4A] mb-4 flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gray-400" />
                      To Do ({tasks.filter(t => t.status === 'todo').length})
                    </h3>
                    <div className="space-y-2">
                      {tasks.filter(t => t.status === 'todo').map((task) => (
                        <TaskCard key={task.task_id} task={task} onStatusChange={updateTaskStatus} />
                      ))}
                    </div>
                  </div>

                  {/* In Progress */}
                  <div className="bg-white rounded-2xl p-4 shadow-sm">
                    <h3 className="font-body font-medium text-[#1A3A4A] mb-4 flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500" />
                      In Progress ({tasks.filter(t => t.status === 'in_progress').length})
                    </h3>
                    <div className="space-y-2">
                      {tasks.filter(t => t.status === 'in_progress').map((task) => (
                        <TaskCard key={task.task_id} task={task} onStatusChange={updateTaskStatus} />
                      ))}
                    </div>
                  </div>

                  {/* Done */}
                  <div className="bg-white rounded-2xl p-4 shadow-sm">
                    <h3 className="font-body font-medium text-[#1A3A4A] mb-4 flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      Done ({tasks.filter(t => t.status === 'done').length})
                    </h3>
                    <div className="space-y-2">
                      {tasks.filter(t => t.status === 'done').map((task) => (
                        <TaskCard key={task.task_id} task={task} onStatusChange={updateTaskStatus} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <div className="space-y-6">
                <h1 className="font-heading text-2xl font-bold text-[#1A3A4A]">Documents</h1>
                
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  {documents.map((doc) => (
                    <div key={doc.document_id} className="flex items-center justify-between p-4 border-b border-[#1A3A4A]/5 last:border-0 hover:bg-[#F8FAFB]">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-[#60B5AF]/10 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-[#60B5AF]" />
                        </div>
                        <div>
                          <p className="font-body font-medium text-[#1A3A4A]">{doc.filename}</p>
                          <p className="font-body text-xs text-[#1A3A4A]/60">
                            {doc.category} • {doc.uploaded_at?.split('T')[0]}
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="text-[#60B5AF]">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {documents.length === 0 && (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 text-[#1A3A4A]/20 mx-auto mb-4" />
                      <p className="font-body text-[#1A3A4A]/40">No documents yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Calendar Tab */}
            {activeTab === 'calendar' && (
              <div className="space-y-6">
                <h1 className="font-heading text-2xl font-bold text-[#1A3A4A]">Upcoming Meetings</h1>
                
                <div className="space-y-4">
                  {meetings.map((meeting) => (
                    <div key={meeting.meeting_id} className="bg-white rounded-2xl p-6 shadow-sm flex items-center gap-6">
                      <div className="text-center min-w-[60px]">
                        <p className="font-heading text-2xl font-bold text-[#1A3A4A]">
                          {new Date(meeting.date).getDate()}
                        </p>
                        <p className="font-body text-xs text-[#1A3A4A]/60 uppercase">
                          {new Date(meeting.date).toLocaleDateString('en', { month: 'short' })}
                        </p>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-body font-medium text-[#1A3A4A]">{meeting.title}</h3>
                        <p className="font-body text-sm text-[#1A3A4A]/60">
                          {meeting.time} • {meeting.duration_minutes} min
                        </p>
                      </div>
                      {meeting.meeting_link && (
                        <a 
                          href={meeting.meeting_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-[#60B5AF] text-white rounded-full font-body text-sm hover:bg-[#60B5AF]/90"
                        >
                          Join
                        </a>
                      )}
                    </div>
                  ))}
                  {meetings.length === 0 && (
                    <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                      <Calendar className="w-12 h-12 text-[#1A3A4A]/20 mx-auto mb-4" />
                      <p className="font-body text-[#1A3A4A]/40">No upcoming meetings</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h1 className="font-heading text-2xl font-bold text-[#1A3A4A]">Notifications</h1>
                
                <div className="bg-white rounded-2xl shadow-sm">
                  {notifications.notifications.map((notif) => (
                    <div 
                      key={notif.notification_id}
                      className={`flex items-start gap-4 p-4 border-b border-[#1A3A4A]/5 last:border-0 ${
                        !notif.read ? 'bg-[#60B5AF]/5' : ''
                      }`}
                    >
                      <div className={`w-3 h-3 rounded-full mt-1.5 ${!notif.read ? 'bg-[#60B5AF]' : 'bg-[#1A3A4A]/20'}`} />
                      <div className="flex-1">
                        <p className="font-body font-medium text-[#1A3A4A]">{notif.title}</p>
                        <p className="font-body text-sm text-[#1A3A4A]/60">{notif.message}</p>
                        <p className="font-body text-xs text-[#1A3A4A]/40 mt-1">
                          {new Date(notif.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  {notifications.notifications.length === 0 && (
                    <div className="text-center py-12">
                      <Bell className="w-12 h-12 text-[#1A3A4A]/20 mx-auto mb-4" />
                      <p className="font-body text-[#1A3A4A]/40">No notifications</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, progress, alert }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm">
    <div className="flex items-center justify-between mb-4">
      <div className={`w-10 h-10 rounded-xl ${alert ? 'bg-red-100' : 'bg-[#60B5AF]/10'} flex items-center justify-center`}>
        <Icon className={`w-5 h-5 ${alert ? 'text-red-600' : 'text-[#60B5AF]'}`} />
      </div>
    </div>
    <p className="font-body text-sm text-[#1A3A4A]/60">{title}</p>
    <p className={`font-heading text-2xl font-bold ${alert ? 'text-red-600' : 'text-[#1A3A4A]'}`}>{value}</p>
    {progress !== undefined && (
      <div className="mt-3">
        <div className="w-full bg-[#1A3A4A]/10 rounded-full h-2">
          <div 
            className="bg-[#60B5AF] h-2 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    )}
  </div>
);

// Task Card Component
const TaskCard = ({ task, onStatusChange }) => {
  const nextStatus = task.status === 'todo' ? 'in_progress' : task.status === 'in_progress' ? 'done' : 'todo';
  
  return (
    <div 
      className="p-3 bg-[#F8FAFB] rounded-lg cursor-pointer hover:bg-[#1A3A4A]/5"
      onClick={() => onStatusChange(task.task_id, nextStatus)}
    >
      <p className={`font-body text-sm ${task.status === 'done' ? 'line-through text-[#1A3A4A]/40' : 'text-[#1A3A4A]'}`}>
        {task.title}
      </p>
      {task.due_date && (
        <p className={`font-body text-xs mt-1 ${
          new Date(task.due_date) < new Date() && task.status !== 'done' ? 'text-red-500' : 'text-[#1A3A4A]/40'
        }`}>
          {task.due_date}
        </p>
      )}
    </div>
  );
};

export default CompanyDashboard;
